import { useState, useEffect, useRef } from 'react';
import { communityApi } from './communityApi';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';
import { Lock, Users, Target, Search, X, Loader2, Shield } from 'lucide-react';

const CATEGORIES = [
  { key: 'investissement', label: 'Portefeuille investissement', desc: 'ETF, Actions, Crypto', allowCommunity: true },
  { key: 'epargne', label: 'Épargne', desc: 'Livrets, assurance-vie', allowCommunity: true },
  { key: 'immobilier', label: 'Immobilier', desc: 'Biens, SCPI', allowCommunity: true },
  { key: 'global_patrimoine', label: 'Patrimoine global complet', desc: 'Toutes catégories + dettes + bilan', allowCommunity: false },
];

const VISIBILITY_OPTIONS = [
  { key: 'private', icon: Lock, label: 'Privé', color: 'text-cap-text-muted' },
  { key: 'community', icon: Users, label: 'Communauté', color: 'text-[#4f8eff]' },
  { key: 'selective', icon: Target, label: 'Sélectif', color: 'text-amber-400' },
];

const MemberSearch = ({ members, onAdd, onRemove }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const search = async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { setResults(await communityApi.searchMembers(q)); }
    catch { setResults([]); }
    finally { setLoading(false); }
  };

  const handleInput = (v) => {
    setQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  return (
    <div className="mt-3">
      <label className="block text-xs text-cap-text-muted mb-1">Membres sélectionnés</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {members.map(m => (
          <span key={m} className="text-[10px] px-2 py-1 rounded bg-amber-400/10 text-amber-400 flex items-center gap-1">
            @{m} <button onClick={() => onRemove(m)}><X className="w-2.5 h-2.5" /></button>
          </span>
        ))}
      </div>
      <div className="relative">
        <div className="flex items-center bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-8">
          <Search className="w-3.5 h-3.5 text-cap-text-muted mr-2" />
          <input className="flex-1 bg-transparent text-white text-xs outline-none placeholder:text-cap-text-muted"
            placeholder="Rechercher un membre..." value={query} onChange={e => handleInput(e.target.value)} />
          {loading && <Loader2 className="w-3 h-3 animate-spin text-cap-text-muted" />}
        </div>
        {results.length > 0 && (
          <div className="absolute z-50 top-full mt-1 w-full bg-[#1a1d27] border border-white/10 rounded-lg shadow-xl max-h-32 overflow-y-auto">
            {results.filter(r => !members.includes(r.username)).map(r => (
              <button key={r.id} onClick={() => { onAdd(r.username); setQuery(''); setResults([]); }}
                className="w-full text-left px-3 py-2 hover:bg-white/5 text-white text-xs">@{r.username}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SharingSettingsModal = ({ open, onClose }) => {
  const [settings, setSettings] = useState({
    investissement: 'private', epargne: 'private', immobilier: 'private',
    global_patrimoine: 'private', selective_members: [],
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSelective, setShowSelective] = useState(false);

  useEffect(() => {
    if (!open) return;
    communityApi.getSharing().then(d => {
      setSettings(d);
      setShowSelective(Object.values(d).some(v => v === 'selective'));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const updateField = (key, val) => {
    setSettings(prev => {
      const next = { ...prev, [key]: val };
      setShowSelective(Object.entries(next).some(([k, v]) => k !== 'selective_members' && v === 'selective'));
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await communityApi.updateSharing(settings);
      toast.success('Paramètres de partage mis à jour');
      onClose();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const handleRequestFounder = async () => {
    try {
      await communityApi.requestFounderAnalysis();
      toast.success('Demande envoyée à @wazzl !');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#141720] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="sharing-settings-modal">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-lg font-bold">Configurer mon partage</DialogTitle>
          <DialogDescription className="text-cap-text-secondary text-sm">Choisissez ce que la communauté peut voir</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4 mt-4">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cap-text-muted" /></div> : (
            <>
              {CATEGORIES.map(cat => (
                <div key={cat.key} className="bg-[#0d0f14] border border-white/5 rounded-xl p-4" data-testid={`sharing-${cat.key}`}>
                  <div className="mb-2">
                    <p className="text-white text-sm font-semibold">{cat.label}</p>
                    <p className="text-cap-text-muted text-[10px]">{cat.desc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {VISIBILITY_OPTIONS.filter(o => cat.allowCommunity || o.key !== 'community').map(opt => {
                      const Icon = opt.icon;
                      const active = settings[cat.key] === opt.key;
                      return (
                        <button key={opt.key} onClick={() => updateField(cat.key, opt.key)}
                          className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-medium transition-all ${active ? 'bg-white/10 border border-white/20 text-white' : 'bg-white/[0.02] border border-transparent text-cap-text-muted hover:bg-white/5'}`}
                          data-testid={`vis-${cat.key}-${opt.key}`}>
                          <Icon className={`w-3.5 h-3.5 ${active ? opt.color : ''}`} /> {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {showSelective && (
                <MemberSearch
                  members={settings.selective_members || []}
                  onAdd={m => setSettings(prev => ({ ...prev, selective_members: [...(prev.selective_members || []), m] }))}
                  onRemove={m => setSettings(prev => ({ ...prev, selective_members: (prev.selective_members || []).filter(x => x !== m) }))}
                />
              )}

              {/* Founder analysis request */}
              <button onClick={handleRequestFounder}
                className="w-full bg-gradient-to-r from-amber-900/30 to-amber-800/20 border border-amber-500/20 rounded-xl p-4 text-left hover:border-amber-500/40 transition-colors"
                data-testid="request-founder-btn">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-300 text-sm font-bold">Demander une analyse au fondateur</span>
                </div>
                <p className="text-amber-400/60 text-[10px]">Envoie une demande à @wazzl pour analyser votre patrimoine</p>
              </button>

              <div className="flex justify-end pt-2 border-t border-white/5">
                <button onClick={handleSave} disabled={saving}
                  className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-9 px-6 rounded-lg disabled:opacity-40 flex items-center gap-1"
                  data-testid="save-sharing-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Enregistrer
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharingSettingsModal;
